// Header.js
// Location: src/components/Header.js
// Main navigation header with stats

import React from 'react';
import { Home, BookOpen, Music, Flame, TrendingUp, Award } from 'lucide-react';
import { getActiveSongs, getCompletedSongsCount } from '../data/DrumData.v2';

/**
 * Header Component
 * Displays navigation tabs and user stats (streak, sessions, songs)
 * 
 * @param {string} currentScreen - Current active screen ('home', 'library', 'songs')
 * @param {Function} onNavigate - Callback when navigation tab is clicked
 * @param {Object} stats - User statistics { streak, sessions, lastPracticeDate }
 * @param {Array} songs - Array of all songs for calculating completed count
 */
const Header = ({ currentScreen, onNavigate, stats, songs }) => {
  const activeSongs = getActiveSongs(songs);
  const completedCount = getCompletedSongsCount(songs);

  return (
    <header className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        {/* Left side: Logo + Navigation */}
        <div className="flex items-center gap-8">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <img 
              src="/logo.png" 
              alt="DrumFlow Logo" 
              className="h-10"
            />
        </div>
          
          {/* Navigation Tabs */}
          <nav className="flex gap-2">
            <button
              onClick={() => onNavigate('home')}
              className={`px-6 py-2 rounded-xl flex items-center gap-2 transition-all ${
                currentScreen === 'home' 
                  ? 'bg-purple-100 text-purple-700 font-medium' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              aria-current={currentScreen === 'home' ? 'page' : undefined}
            >
              <Home className="w-4 h-4" />
              <span>Routine</span>
            </button>
            
            <button
              onClick={() => onNavigate('library')}
              className={`px-6 py-2 rounded-xl flex items-center gap-2 transition-all ${
                currentScreen === 'library' 
                  ? 'bg-purple-100 text-purple-700 font-medium' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              aria-current={currentScreen === 'library' ? 'page' : undefined}
            >
              <BookOpen className="w-4 h-4" />
              <span>Library</span>
            </button>
            
            <button
              onClick={() => onNavigate('songs')}
              className={`px-6 py-2 rounded-xl flex items-center gap-2 transition-all ${
                currentScreen === 'songs' 
                  ? 'bg-purple-100 text-purple-700 font-medium' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              aria-current={currentScreen === 'songs' ? 'page' : undefined}
            >
              <Music className="w-4 h-4" />
              <span>Songs</span>
            </button>
          </nav>
        </div>
        
        {/* Right side: Stats */}
        <div className="flex items-center gap-6">
          {/* Streak */}
          <div className="flex items-center gap-2 text-sm" title="Practice streak">
            <Flame className="w-4 h-4 text-orange-500" />
            <span className="font-semibold text-gray-700">{stats.streak}</span>
            <span className="text-gray-500">days</span>
          </div>
          
          {/* Total Sessions */}
          <div className="flex items-center gap-2 text-sm" title="Total practice sessions">
            <TrendingUp className="w-4 h-4 text-blue-500" />
            <span className="font-semibold text-gray-700">{stats.sessions}</span>
            <span className="text-gray-500">sessions</span>
          </div>
          
          {/* Songs Progress */}
          <div className="flex items-center gap-2 text-sm" title="Songs completed">
            <Award className="w-4 h-4 text-green-500" />
            <span className="font-semibold text-gray-700">
              {completedCount}/{activeSongs.length}
            </span>
            <span className="text-gray-500">songs</span>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;